import axios from "axios";
import {
  getPlatformSettings,
  fetchPlatformSettings,
  getFairSeedConfig,
} from "../lib/platformSettings";
import { FAIR_INVESTMENT_CONFIG } from "../config/fairSettings";
import { useState, useEffect, useRef } from "react";
import { useMemo } from "react";
import { CapabilityGate } from "../access/AccessContext";
import { PERMISSIONS } from "../access/capabilities";


import ExecutiveCard from "../components/executive/ExecutiveCard";
import ExecutiveSection from "../components/executive/ExecutiveSection";
import ExecutiveChartCard from "../components/executive/ExecutiveChartCard";
import ExecutiveRecommendationPanel from "../components/executive/ExecutiveRecommendationPanel";

import { probabilityTooltip, lossLabelFormatter} from "../utils/chartFormatters";
import { formatMoney, formatRatio } from "../utils/formatters";
import LoadingOrbit from "../components/LoadingOrbit";
import AnalyticalDecisionJourney from "../components/analytical_journey/AnalyticalDecisionJourney";

import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";

const formatPercent = (value) => {
  const number = Number(value || 0);
  return `${(number * 100).toFixed(0)}%`;
};

const getRoiLabel = (roi) => {
  if (roi === null || roi === undefined) return "Not available";
  if (roi >= 20) return "Exceptional return";
  if (roi >= 10) return "High return";
  if (roi >= 3) return "Positive return";
  if (roi > 0) return "Marginal return";
  return "Negative return";
};

function MetricComparisonCard({
  title,
  baseline,
  control,
  reduction,
  showControl,
  formatMoney,
  currency,
  isHighlighted = false,
}) {
  return (
    <div
      className={`rounded-2xl p-5 shadow-sm ${
        isHighlighted
          ? "bg-blue-50 border-2 border-blue-600"
          : "bg-white border border-gray-200"
      }`}
    >
      <p className="text-xs text-gray-500 uppercase mb-3">{title}</p>

      <div className="flex items-baseline gap-2">
        <span className="text-2xl font-semibold text-gray-900">
          {formatMoney(baseline, currency)}
        </span>

        {showControl && control !== undefined && (
          <>
            <span className="text-gray-400 text-lg">→</span>
            <span className="text-2xl font-semibold text-green-700">
              {formatMoney(control, currency)}
            </span>
          </>
        )}
      </div>

      {showControl && reduction !== undefined && (
        <div className="mt-4 inline-flex items-center rounded-full bg-green-50 px-3 py-1">
          <span className="text-xs font-medium text-green-700">
            Reduction {formatMoney(reduction, currency)}
          </span>
        </div>
      )}
    </div>
  );
}

function FairAssetTable({ assets, formatMoney, currency }) {
  if (!assets || assets.length === 0) return null;

  return (
    <div className="mt-8 border border-gray-200 rounded-2xl overflow-hidden">
      <div className="px-5 py-4 bg-gray-50 border-b border-gray-200">
        <h3 className="text-lg font-semibold text-gray-900">
          FAIR Asset Loss Parameters
        </h3>
        <p className="text-xs text-gray-500 mt-1">
          Loss magnitude inputs used by the simulation model.
        </p>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-white border-b border-gray-200">
            <tr className="text-left text-xs text-gray-500 uppercase">
              <th className="px-5 py-3">Asset</th>
              <th className="px-5 py-3">Type</th>
              <th className="px-5 py-3">LEF</th>
              <th className="px-5 py-3">Primary Loss</th>
              <th className="px-5 py-3">Secondary Loss</th>
              <th className="px-5 py-3">Total Exposure</th>
            </tr>
          </thead>

          <tbody className="divide-y divide-gray-100">
            {assets.map((asset) => {
              const primaryMin = asset.primary_loss_min || 0;
              const primaryMax = asset.primary_loss_max || 0;
              const secondaryMin = asset.secondary_loss_min || 0;
              const secondaryMax = asset.secondary_loss_max || 0;

              const totalMin = primaryMin + secondaryMin;
              const totalMax = primaryMax + secondaryMax;

              return (
                <tr key={asset.id} className="hover:bg-gray-50">
                  <td className="px-5 py-3 font-medium text-gray-900">
                    {asset.name}
                  </td>

                  <td className="px-5 py-3 text-gray-600">
                    {asset.asset_type || "—"}
                  </td>

                  <td className="px-5 py-3 text-gray-600">
                    {Number(asset.lef || 0).toFixed(2)}
                  </td>

                  <td className="px-5 py-3 text-gray-600">
                    {formatMoney(primaryMin, currency)} – {formatMoney(primaryMax, currency)}
                  </td>

                  <td className="px-5 py-3 text-gray-600">
                    {formatMoney(secondaryMin, currency)} – {formatMoney(secondaryMax, currency)}
                  </td>

                  <td className="px-5 py-3 font-semibold text-gray-900">
                    {formatMoney(totalMin, currency)} – {formatMoney(totalMax, currency)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function RiskQuantification() {

//  const [incidentType, setIncidentType] = useState("All");
  const [assetType, setAssetType] = useState("All");
  const [asset, setAsset] = useState("All");
  const [analysis, setAnalysis] = useState(null);
  const fair = analysis?.fair;
  const sensitivity = analysis?.fair_sensitivity;
  const [filterOptions, setFilterOptions] = useState({
    asset_types: ["All"],
    assets: [],
  });
  const [simulateControl, setSimulateControl] = useState(false);
  const [appliedSimulateControl, setAppliedSimulateControl] = useState(false);
  const [investment, setInvestment] = useState(0);
  const [selectedInvestment, setSelectedInvestment] = useState(0);

  const [activeScenarios, setActiveScenarios] = useState([]);
  const [selectedScenarioId, setSelectedScenarioId] = useState("");
  const [loading, setLoading] = useState(false);

  const [settings, setSettings] = useState(getPlatformSettings());
  const [settingsLoaded, setSettingsLoaded] = useState(false);
 
  const analysisRequestId = useRef(0);

  const [presentationMode, setPresentationMode] = useState(false);

  useEffect(() => {
    let isActive = true;

    const loadSettings = async () => {
      try {
        const freshSettings = await fetchPlatformSettings();

        if (!isActive) return;

        setSettings(freshSettings);
      } catch (err) {
        console.error(
          "Error loading platform settings:",
          err
        );
      } finally {
        if (isActive) {
          setSettingsLoaded(true);
        }
      }
    };

    loadSettings();

    return () => {
      isActive = false;
    };
  }, []);

  const fairParams = useMemo(() => ({
    simulations: Number(settings.simulations || 5000),
    seed: getFairSeedConfig(settings).seed,
  }), [
    settings.simulations,
    settings.seedMode,
    settings.seedValue,
  ]);

  const formatMillions = (value) => {
    const number = Number(value || 0);
    return formatMoney(number, settings.currency);
  };

  const hasInvestment = appliedSimulateControl && investment > 0;

  const scenarioImpact = analysis?.impact || null;


  const scenarioEalReduction =
   scenarioImpact?.eal_reduction ?? 0;

  const scenarioNetBenefit =
    scenarioImpact?.net_benefit ?? 0;

  const scenarioRoi =
    scenarioImpact?.roi ?? 0;


  // =====================================================
  // Exposure driver interpretation
  // =====================================================

  const threatFrequency =
    fair?.tef || 0;

  const vulnerability =
    fair?.vulnerability || 0;

  const lossMagnitude =
    analysis?.baseline?.expected_annual_loss || 0;

  const drivers = [
    {
      key: "Threat Frequency",
      value: threatFrequency,
    },
    {
      key: "Vulnerability",
      value: vulnerability,
    },
    {
      key: "Loss Magnitude",
      value: lossMagnitude,
    },
  ];

  const dominantDriver =
    drivers.sort((a, b) => b.value - a.value)[0]?.key || "Unknown";

  const getExposureDriverInterpretation = ({
    dominantDriver,
  }) => {

    if (dominantDriver === "Vulnerability") {
      return {
        title: "Control Weakness Dominates Exposure",

        summary:
          "Current exposure is primarily driven by operational vulnerability and control effectiveness gaps.",

        interpretation:
          "Threat activity remains moderate, but current defensive maturity is not yet sufficient to materially reduce business service exposure.",

        businessImpact:
          "Primary financial exposure is associated with operational interruption, recovery delays and resilience gaps rather than attack frequency.",
      };
    }

    if (dominantDriver === "Threat Frequency") {
      return {
        title: "Threat Activity Dominates Exposure",

        summary:
          "Current exposure is primarily driven by elevated threat frequency and repeated attack activity.",

        interpretation:
          "Even moderate vulnerabilities may generate material exposure when attack attempts occur persistently over time.",

        businessImpact:
          "Reducing attack surface and improving monitoring capability would materially reduce expected loss exposure.",
      };
    }

    return {
      title: "Loss Severity Dominates Exposure",

      summary:
        "Current exposure is primarily driven by the financial magnitude of a potential disruption event.",

      interpretation:
        "Even low-frequency events may produce material financial impact due to operational dependency concentration.",

      businessImpact:
        "Business continuity, resilience and recovery capability become strategic priorities for exposure reduction.",
    };
  };

  const driverInterpretation =
    getExposureDriverInterpretation({
      dominantDriver,
    });

  // =====================================================
  // Risk Appetite
  // =====================================================

  const riskAppetite = {
    p95: settings.riskAppetiteP95 || 5000000,
    eal: settings.riskAppetiteEAL || 1500000,
  };

  const confidenceMetric =
    settings.confidenceLevel || "P95";

  const currentTailRisk =
    confidenceMetric === "P90"
      ? analysis?.baseline?.p90 || 0
      : confidenceMetric === "P99"
      ? analysis?.baseline?.p99 || 0
      : analysis?.baseline?.p95 || 0;

  const currentEAL =
    analysis?.baseline?.expected_annual_loss || 0;

  const appetiteStatus =
    currentTailRisk <= riskAppetite.p95
      ? "Within Appetite"
      : "Above Appetite";

  const appetiteColor =
    currentTailRisk <= riskAppetite.p95
      ? "text-green-700"
      : "text-red-700";


  // =====================================================
  // Residual Risk Classification
  // =====================================================

  const residualRiskLevel =
    currentTailRisk > riskAppetite.p95 * 1.5
      ? "Critical"
      : currentTailRisk > riskAppetite.p95
      ? "Elevated"
      : "Controlled";

  const residualRiskColor =
    residualRiskLevel === "Critical"
      ? "text-red-700 bg-red-50 border-red-200"
      : residualRiskLevel === "Elevated"
      ? "text-amber-700 bg-amber-50 border-amber-200"
      : "text-green-700 bg-green-50 border-green-200";

  const residualRiskMessage =
    residualRiskLevel === "Critical"
      ? "Residual exposure materially exceeds board tolerance and requires executive attention."
      : residualRiskLevel === "Elevated"
      ? "Residual exposure is above appetite but can be reduced through targeted resilience investment."
      : "Residual exposure is currently within the defined board tolerance.";


  // =====================================================
  // Executive Narrative
  // =====================================================

  const baselineCurvePct = (analysis?.curve || []).map((p) => ({
    ...p,
    exceedance_pct: Number(p.exceedance_probability || 0) * 100,
  }));

  const controlCurvePct = (analysis?.control_curve || []).map((p) => ({
    ...p,
    exceedance_pct: Number(p.exceedance_probability || 0) * 100,
  }));


  const activeConfidenceMetric =
    settings.confidenceLevel || "P95";

  const isActiveConfidence = (metric) =>
    activeConfidenceMetric === metric;

  const executiveNarrative = (() => {
    if (!analysis?.baseline) {
      return null;
    }

    if (residualRiskLevel === "Critical") {
      return `This scenario represents material exposure above board tolerance. The primary concern is severe loss potential, with ${formatMoney(
        currentTailRisk
      )} ${confidenceMetric} exposure against a tolerance of ${formatMoney(
        riskAppetite.p95, settings.currency
      )}. Executive action should focus on resilience, recovery capability and exposure reduction.`;
    }

    if (residualRiskLevel === "Elevated") {
      return `This scenario remains above the defined risk appetite. While expected annual loss is ${formatMoney(
        currentEAL
      )}, severe loss exposure could reach ${formatMoney(
         currentTailRisk, settings.currency
      )}. Targeted investment should focus on reducing residual exposure and improving operational resilience.`;
    }

    return `This scenario is currently within board tolerance. Continued monitoring is recommended to ensure changes in threat activity, vulnerability or loss magnitude do not increase residual exposure.`;
  })();


  useEffect(() => {
    axios
      .get("/api/digital-risk/fair-scenarios/", {
        params: { 
          status: "Active", 
        },
          
      })
      .then((res) => setActiveScenarios(res.data))
      .catch(console.error);
  }, [fairParams]);


  useEffect(() => {
    axios
      .get("/api/digital-risk/dashboard/risk-quantification-filters/", {
        params: {
          asset_type: assetType,
        },
      })
      .then((res) => {
        setFilterOptions(res.data);
        setAsset("All");
      })
      .catch((err) => console.error("Error loading filters:", err));
  }, [assetType, fairParams]);



  const runAnalysis = ({
    scenarioSnapshot = selectedScenarioId,
    simulateControlSnapshot = simulateControl,
    investmentSnapshot = selectedInvestment,
  } = {}) => {
    if (!settingsLoaded) return;
    if (!scenarioSnapshot) {
      analysisRequestId.current += 1;
      setAnalysis(null);
      return;
    }

    const requestId = ++analysisRequestId.current;

    setLoading(true);

    axios
      .get("/api/digital-risk/dashboard/risk-quantification-analysis/", {
        params: {
          scenario_id: scenarioSnapshot,
          simulate_control: simulateControlSnapshot,
          investment: simulateControlSnapshot ? investmentSnapshot : 0,
          ...fairParams,
        },
      })
      .then((res) => {
        if (requestId === analysisRequestId.current) {
          setAnalysis(res.data);
          setAppliedSimulateControl(simulateControlSnapshot);
          setInvestment(simulateControlSnapshot ? investmentSnapshot : 0);
        }
      })
      .catch((err) => {
        if (requestId === analysisRequestId.current) {
          console.error("Error loading risk quantification:", err);
        }
      })
      .finally(() => {
        if (requestId === analysisRequestId.current) {
          setLoading(false);
        }
      });

  };

  useEffect(() => {
    if (!settingsLoaded) return;
    setSimulateControl(false);
    setAppliedSimulateControl(false);
    setSelectedInvestment(0);
    setInvestment(0);
    runAnalysis({ scenarioSnapshot: selectedScenarioId, simulateControlSnapshot: false, investmentSnapshot: 0 });
    // Scenario/settings changes establish the governed baseline, as in Risk Investment Optimization.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  },[
    settingsLoaded,
    selectedScenarioId,
    fairParams.simulations,
    fairParams.seed,
  ]);

  const investmentStep = Number(settings?.investmentStep || 50000);

  const investmentCeilingRatio = Number(settings?.investmentCeilingRatio || 0.75);

  const portfolioEalForCeiling = Number(
    analysis?.expected_annual_loss ??
    analysis?.baseline?.expected_annual_loss ??
    0
  );

  const dynamicInvestmentMax = Math.max(
    FAIR_INVESTMENT_CONFIG.max,
    Math.ceil(
      (portfolioEalForCeiling * investmentCeilingRatio) / investmentStep
    ) * investmentStep
  );

  const normalizeInvestment = (value) => {
    return Math.round(Number(value || 0) / investmentStep) * investmentStep;
  };

  const analysisMatchesSelectedScenario = Boolean(
    selectedScenarioId &&
    analysis?.metadata?.scenario_id !== undefined &&
    String(analysis.metadata.scenario_id) === String(selectedScenarioId)
  );

  const selectedScenarioAnalysis = analysisMatchesSelectedScenario
    ? analysis.fair_scenario
    : null;

  if (activeScenarios.length === 0) {
    return (
      <div className="p-6 space-y-6">
  
      <div  className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <div>
          <p className="text-xs uppercase tracking-[0.25em] text-slate-400">
            Strategic Intelligence
          </p>
          <h1 className="text-2xl font-semibold text-slate-900 mt-1">
            Risk Quantification
          </h1>
          <p className="text-sm text-slate-500 mt-1">
              Quantify financial exposure, probable loss and risk frequency across governed digital risk scenarios.
          </p>
          <p className="mt-2 text-xs text-slate-500">
            Monte Carlo: {settings.simulations.toLocaleString()} simulations ·
            Confidence: {settings.confidenceLevel} ·
            Loss model: {settings.lossModel}
          </p>
        </div>
        <div className="text-left xl:text-right mr-4">
          <p className="text-sm text-slate-400">Risk Confidence Level</p>
          <p className="text-4xl font-bold mt-1">{confidenceMetric}</p>
        </div>

      </div>

      <AnalyticalDecisionJourney currentStage="quantify" />

        <div className="bg-white border rounded-2xl p-10 text-center">

          <h2 className="text-2xl font-semibold text-gray-900">
            No Active Risk Scenarios
          </h2>

          <p className="text-gray-500 mt-3 max-w-2xl mx-auto">
            Risk quantification requires active FAIR scenarios to calculate
            expected annual loss, exceedance curves and investment optimization.
          </p>

          <div className="mt-6 text-sm text-gray-400">
            Create and activate FAIR scenarios to begin analysis.
          </div>

        </div>

      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">

      <div  className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <div>
          <p className="text-xs uppercase tracking-[0.25em] text-slate-400">
            Strategic Intelligence
          </p>
          <h1 className="text-2xl font-semibold text-slate-900 mt-1">
            Risk Quantification
          </h1>
          <p className="text-sm text-slate-500 mt-1">
              Quantify financial exposure, probable loss and risk frequency across governed digital risk scenarios.
          </p>
          <p className="mt-2 text-xs text-slate-500">
            Monte Carlo: {settings.simulations.toLocaleString()} simulations ·
            Confidence: {settings.confidenceLevel} ·
            Loss model: {settings.lossModel}
          </p>
        </div>
        <div className="text-left xl:text-right mr-4">
          <p className="text-sm text-slate-400">Risk Confidence Level</p>
          <p className="text-4xl font-bold mt-1">{confidenceMetric}</p>
        </div>

      </div>

      <AnalyticalDecisionJourney currentStage="quantify" />

      <div className="flex justify-end">
        <button
          onClick={() => {
            const nextMode = !presentationMode;
            setPresentationMode(nextMode);

            if (nextMode && !document.fullscreenElement) {
              document.documentElement.requestFullscreen();
            }

            if (!nextMode && document.fullscreenElement) {
              document.exitFullscreen();
            }
          }}
          className={`
            px-4 py-2 rounded-xl text-sm border transition
            ${
              presentationMode
                ? "bg-slate-900 text-white border-slate-900"
                : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50"   
            }
          `}
        >
           🖥️ {presentationMode ? "Exit Presentation" : "Presentation Mode"}
        </button>
      </div>

      <div>
        <label className="text-sm ml-2 text-gray-500">FAIR Scenario</label>
        <br />

        <select
          value={selectedScenarioId}
          onChange={(e) => setSelectedScenarioId(e.target.value)}
          className="w-full max-w-4xl border rounded-xl px-4 py-3 mt-2 bg-white"
        >
          <option value="">Select active FAIR scenario</option>

          {activeScenarios.map((scenario) => (
            <option key={scenario.id} value={scenario.id}>
              {scenario.name}
            </option>
          ))}
        </select>

        {selectedScenarioId &&
          (() => {
            const selectedScenario = activeScenarios.find(
              (s) => String(s.id) === String(selectedScenarioId)
            );

            if (!selectedScenario) return null;

            return (
              <div className="mt-3 rounded-xl border bg-slate-50 px-4 py-3 text-sm">
                <div>
                  <span className="font-medium">Incident Type:</span>{" "}
                  {selectedScenarioAnalysis?.incident_type || "—"}
                  <span className="ml-1 font-medium">| Asset:</span>{" "}
                  {selectedScenarioAnalysis?.asset_name || "—"}
                  <span className="ml-1 font-medium">| Status:</span>{" "}
                  {selectedScenario.status}
                </div>
              </div>
            );
          })()}

      </div>

      
      {selectedScenarioId &&
      <div className="relative">

        {loading && (
          <div className="fixed left-[100px] right-0 bottom-0 top-[260px] z-40 flex items-start justify-center  pt-20">
            <LoadingOrbit
              message=""
              submessage=""
            />
          </div>
        )}

        {/* TODO el contenido actual */}

        {fair && (
          <div className="bg-white border border-gray-200 rounded-2xl p-5 mb-6">
            <p className="text-lg font-semibold text-gray-900 mb-3">
              What drives your risk
            </p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm">
                <p className="text-xs text-gray-500 mb-1">Threat Exposure</p>
                <p className="text-xl font-semibold text-gray-900">
                  {fair.tef}
                </p>
                <p className="text-xs text-gray-400">
                  Relevant threat attempts per year
                </p>
              </div>

              <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm">
                <p className="text-xs text-gray-500 mb-1">Control Weakness</p>
                <p className="text-xl font-semibold text-orange-600">
                  {(fair.vulnerability * 100).toFixed(0)}%
                </p>
                <p className="text-xs text-gray-400">
                  Probability controls fail
                </p>
              </div>

              <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm">
                <p className="text-xs text-gray-500 mb-1">Risk Realization</p>
                <p className="text-xl font-semibold text-red-600">
                  {fair.lef}
                </p>
                <p className="text-xs text-gray-400">Loss events per year</p>
              </div>
            </div>
          </div>
        )}

        {sensitivity && (
        
          <div className="bg-white border border-gray-200 rounded-2xl p-5 mb-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-3">
              Key Exposure Drivers
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">

              {Object.entries(sensitivity.drivers).map(([key, item]) => {
                const isTop = sensitivity.biggest_driver.key === key;

                return (
                  <div
                    key={key}
                    className={`p-4 rounded-2xl border ${
                      isTop
                        ? "bg-blue-50 border-blue-200"
                        : "bg-gray-50 border-gray-200"
                    }`}
                  >
                    <p className="text-sm font-semibold text-gray-900 mb-1">
                      {item.label}
                    </p>

                    <p className="text-lg font-bold text-gray-900">
                      {item.reduction_pct.toFixed(1)}%
                    </p>

                    <p className="text-xs text-gray-500">Risk reduction</p>

                    {isTop && (
                      <div className="mt-2 text-xs mb-1 font-semibold text-blue-600">
                        Highest impact
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        
        )}

      
       {sensitivity && (
        <>
        <div className=" border rounded-2xl p-5 bg-slate-50">

          <p className="text-sm font-semibold text-slate-900">
            {driverInterpretation.title}
          </p>

          <p className="text-sm text-gray-700 mt-3 leading-relaxed">
            {driverInterpretation.summary}
          </p>

          <p className="text-sm text-gray-600 mt-3 leading-relaxed">
            {driverInterpretation.interpretation}
          </p>

          <div className="mt-4 border-t pt-3">
            <p className="text-xs uppercase tracking-wide text-gray-500">
              Business Implication
            </p>

            <p className="text-sm text-slate-800 mt-1 leading-relaxed">
              {driverInterpretation.businessImpact}
            </p>
          </div>
        </div>


        <div className="mt-5 border rounded-2xl p-5 bg-white">

          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-semibold text-slate-900">
                Board Risk Appetite
              </p>

              <p className="text-sm text-gray-600 mt-2">
                Current portfolio exposure compared
                against defined executive tolerance thresholds.
              </p>
            </div>

            <div className={`text-sm font-semibold ${appetiteColor}`}>
              {appetiteStatus}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4 mt-5">

            <div className="border rounded-xl p-4 bg-slate-50">
              <p className="text-xs text-gray-500 uppercase">
                Current P95 Exposure
              </p>

              <p className="text-xl font-bold mt-2">
                {formatMoney(currentTailRisk, settings.currency)}
              </p>
            </div>

            <div className="border rounded-xl p-4 bg-slate-50">
              <p className="text-xs text-gray-500 uppercase">
                Board Tolerance Threshold
              </p>

              <p className="text-xl font-bold mt-2">
                {formatMoney(riskAppetite.p95, settings.currency)}
              </p>
            </div>
          </div>


          <div className={`mt-5 border rounded-2xl p-5 ${residualRiskColor}`}>
            <p className="text-sm font-semibold">
              Residual Risk Classification
            </p>

            <p className="text-2xl font-bold mt-2">
              {residualRiskLevel}
            </p>

            <p className="text-sm mt-2 leading-relaxed">
              {residualRiskMessage}
            </p>
          </div>

          {executiveNarrative && (
            <div className="mt-5 border rounded-2xl p-5 bg-blue-50 border-blue-100">
              <p className="text-sm font-semibold text-blue-900">
                Executive Interpretation
              </p>

              <p className="text-sm text-blue-800 mt-2 leading-relaxed">
                {executiveNarrative}
              </p>
            </div>
          )}

        </div>
        </>
       )}


       <div className="bg-white border border-gray-200 rounded-2xl mt-4 p-6 shadow-sm">
       
        {analysis?.baseline && (

          
          <div className="mb-6 p-4 rounded-xl bg-blue-50 border border-blue-100">
            <p className="text-sm text-blue-800 font-medium">
              This scenario represents an expected annual loss of{" "}
              <strong>{formatMoney(analysis.baseline.expected_annual_loss, settings.currency)}</strong>.
              However, in severe conditions, losses could reach{" "}
              <strong>{formatMoney(analysis.baseline.p95, settings.currency)}</strong>.
            </p>
          </div>
        )}

        {analysis && (
          <>

            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              Scenario Loss Analysis
            </h2>

            <p className="text-sm text-gray-500 mb-6">
              Estimate how security investment changes expected loss, tail risk and transformation capacity.
            </p>

            <p className="text-sm font-semibold text-slate-900">
              Before vs After Control Impact
            </p>

            <p className="text-sm text-gray-600 mt-2">
              Shows how the simulated investment changes financial exposure and residual risk.
            </p>


            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">

              
              <MetricComparisonCard
                title="Expected Annual Loss"
                baseline={analysis.baseline?.expected_annual_loss}
                control={analysis.with_control?.expected_annual_loss}
                reduction={analysis.impact?.eal_reduction}
                showControl={hasInvestment}
                formatMoney={formatMoney}
                currency={settings.currency}
              />

              <MetricComparisonCard
                title="P90 Loss"
                baseline={analysis.baseline?.p90}
                control={analysis.with_control?.p90}
                reduction={analysis.impact?.p90_reduction}
                showControl={hasInvestment}
                formatMoney={formatMoney}
                isHighlighted={isActiveConfidence("P90")}
                currency={settings.currency}
              />

              <MetricComparisonCard
                title="P95 Loss"
                baseline={analysis.baseline?.p95}
                control={analysis.with_control?.p95}
                reduction={analysis.impact?.p95_reduction}
                showControl={hasInvestment}
                formatMoney={formatMoney}
                isHighlighted={isActiveConfidence("P95")}
                currency={settings.currency}
              />

              <MetricComparisonCard
                title="P99 Loss"
                baseline={analysis.baseline?.p99}
                control={analysis.with_control?.p99}
                reduction={analysis.impact?.p99_reduction}
                showControl={hasInvestment}
                formatMoney={formatMoney}
                isHighlighted={isActiveConfidence("P99")}
                currency={settings.currency}
              />
            </div>

            <ExecutiveSection
              title="Control Impact Simulation"
              subtitle="Compare baseline exposure against a simulated control investment."
            >

                <label className="flex items-center gap-2 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={simulateControl}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setSimulateControl(checked);
                      setSelectedInvestment(checked ? 500000 : 0);
                    }}
                  />
                  Simulate Control
                </label>
                  <p className="text-xs text-gray-400 mb-4">
                    Model input: LEF {analysis.event_rate} loss events/year ·{" "}
                    {analysis.simulations.toLocaleString()} simulations ·{" "}
                    {analysis.assets.length} assets included
                </p>
           
            </ExecutiveSection>


            {(simulateControl || appliedSimulateControl) && (

              <div className="bg-sky-900 text-white mt-4 rounded-2xl p-6 shadow-sm mb-6">
                <div className="grid grid-cols-1 md:grid-cols-5 gap-6 items-center">
                  <div>
                    <p className="text-sm uppercase tracking-wide text-gray-200">Current Investment</p>
                    <p className="text-4xl font-bold mt-2">
                      {formatMoney(selectedInvestment, settings.currency)}
                    </p>
                  </div>

                  <div className="md:col-span-4">
                    <input
                      type="range"
                      min={FAIR_INVESTMENT_CONFIG.min}
                      max={dynamicInvestmentMax}
                      step={investmentStep}
                      value={selectedInvestment}
                      onChange={(e) =>
                        setSelectedInvestment(normalizeInvestment(e.target.value))
                      }
                      className="
                        w-full h-2 rounded-lg appearance-none cursor-pointer
                        bg-gray-200
                        [&::-webkit-slider-thumb]:appearance-none
                        [&::-webkit-slider-thumb]:h-6
                        [&::-webkit-slider-thumb]:w-6
                        [&::-webkit-slider-thumb]:rounded-full
                        [&::-webkit-slider-thumb]:bg-cyan-400
                        [&::-webkit-slider-thumb]:shadow-lg
                        [&::-webkit-slider-thumb]:border-4
                        [&::-webkit-slider-thumb]:border-[#0B132B]
                      "
                    />

                    <div className="flex justify-between text-xs text-slate-200 mt-2">
                      <span>{formatMoney(FAIR_INVESTMENT_CONFIG.min, settings.currency)}</span>
                      <span>{formatMoney(dynamicInvestmentMax, settings.currency)}</span>
                    </div>
                    <div className="mt-4 flex items-center justify-between gap-4">
                      <p className="text-sm text-slate-100" role="status">
                        {simulateControl !== appliedSimulateControl || selectedInvestment !== investment
                          ? "Changes not yet applied"
                          : "Current assumptions applied"}
                      </p>
                      <CapabilityGate permission={PERMISSIONS.fair.run}><button
                        type="button"
                        onClick={() => runAnalysis()}
                        disabled={loading || (simulateControl === appliedSimulateControl && selectedInvestment === investment)}
                        className="rounded-xl bg-cyan-400 px-4 py-2 text-sm font-semibold text-sky-950 disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        {loading ? "Running..." : "Run Analysis"}
                      </button></CapabilityGate>
                    </div>
                  </div>
                </div>
              </div>
            )}
            
            {/*   ---- Simulation ------   */}
            {hasInvestment && scenarioImpact && (

              <div className="bg-slate-50 border border-slate-200 rounded-2xl p-5">
                <div className="mb-4">
                  <p className="text-sm font-semibold text-gray-900">
                    Investment Efficiency
                  </p>
                  <p className="text-xs text-gray-500">
                    Financial efficiency and value creation from risk reduction.
                  </p>
                </div>

                <div className="grid grid-cols-1 mb-6 md:grid-cols-4 gap-4">
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Investment</p>
                    <p className="text-xl font-semibold text-gray-900">
                      {formatMoney(selectedInvestment, settings.currency)}
                    </p>
                  </div>

                  <div>
                    <p className="text-xs text-gray-500 mb-1">
                      Annual Risk Reduction
                    </p>

                    <p className="text-xl font-semibold text-green-700">
                    {formatPercent(
                      analysis?.baseline?.expected_annual_loss
                        ? scenarioEalReduction /
                            analysis.baseline.expected_annual_loss
                        : 0
                    )}
                    </p>
                  </div>

                  <div>
                    <p className="text-xs text-gray-500 mb-1">Net Benefit</p>
                    <p className="text-xl font-semibold text-green-700">
                      {formatMoney(scenarioNetBenefit || 0, settings.currency)}
                    </p>
                  </div>

                  <div>
                    <p className="text-xs text-gray-500 mb-1">ROI</p>
                    <p className="text-xl font-semibold text-green-700">
                      {scenarioRoi
                        ? `${scenarioRoi.toFixed(1)}x`
                        : "—"}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      {getRoiLabel(scenarioRoi)}
                    </p>
                  </div>
                </div>

                {analysis?.impact && (
                 <ExecutiveRecommendationPanel
                    label={getRoiLabel(scenarioRoi)}
                    description={
                      <>
                        This investment reduces expected exposure by{" "}
                        <strong>{formatMoney(scenarioEalReduction, settings.currency)}</strong>, with a return of{" "}
                        <strong>{scenarioRoi.toFixed(1)}x</strong>.
                      </>
                    }
                    metricLabel="ROI"
                    metricValue={`${scenarioRoi.toFixed(1)}x`}
                    tone={scenarioRoi >= 1 ? "success" : scenarioRoi > 0 ? "warning" : "danger"}
                  />
                )}
              </div>
            )}


            <div className="flex items-center mt-4 justify-between mb-3">

              <div className="flex items-center gap-4 text-xs text-gray-500">
                <div className="flex items-center gap-1">
                  <span className="h-2 w-2 rounded-full bg-blue-500"></span>
                  Baseline
                </div>

                {hasInvestment && (
                  <div className="flex items-center gap-1">
                    <span className="h-2 w-2 rounded-full bg-green-600"></span>
                    With control
                  </div>
                )}
              </div>
            </div>

              <ExecutiveChartCard
                title="Loss Exceedance Curve"
                subtitle="Compare baseline and simulated control loss exceedance."
                height={340}
              >
    
                <LineChart
                  data={baselineCurvePct}
                  margin={{ top: 20, right: 30, left: 40, bottom: 35 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />

                  <XAxis
                    dataKey="loss"
                    type="number"
                    domain={[0, "dataMax"]}
                    tickFormatter={formatMillions}
                    tick={{ fontSize: 12, fill: "#6B7280" }}
                    axisLine={{ stroke: "#E5E7EB" }}
                    tickLine={false}
                  />

                  <YAxis
                    domain={[0, 100]}
                    tickFormatter={(value) => `${value}%`}
                    tick={{ fontSize: 12, fill: "#6B7280" }}
                    axisLine={{ stroke: "#E5E7EB" }}
                    tickLine={false}
                    label={{
                      value: "Probability of Exceedance (%)",
                      angle: -90,
                      position: "insideLeft",
                      style: {
                        textAnchor: "middle",
                        fontSize: 12,
                        fill: "#6B7280",
                      },
                    }}
                  />

                  <Tooltip
                    formatter={probabilityTooltip}
                    labelFormatter={(value) => `Loss: ${formatMoney(value, settings.currency)}`}
                  />

                  <Line
                    type="monotone"
                    dataKey="exceedance_pct"
                    stroke="#4F7DBA"
                    strokeWidth={3}
                    dot={false}
                    name="Baseline"
                  />

                  {hasInvestment && analysis.control_curve && (
                    <Line
                      type="monotone"
                      data={controlCurvePct}
                      dataKey="exceedance_pct"
                      stroke="#16A34A"
                      strokeWidth={3}
                      dot={false}
                      name="With Control"
                    />
                  )}
                </LineChart>
            
              </ExecutiveChartCard>
          

            <div className="h-[360px] mt-4 w-full">
              <FairAssetTable
                assets={analysis.assets}
                formatMoney={formatMoney}
                currency={settings.currency}
              />
            </div>

          </>
        )}
      </div>
      
     </div>
    }
    </div>
  );
}
